import React, { useRef, useState, useCallback } from 'react';
import {
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  useColorModeValue,
  Button,
  Box,
  Icon,
  HStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Progress,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { FaUpload, FaCheckCircle, FaFile } from 'react-icons/fa';
import { useOnboardingStore } from '../../../stores/onboardingStore';
import { useDocumentUpload } from '../../../hooks/useDocumentUpload';
import { DocumentType } from '../../../types/document';

const AcademicStep: React.FC = () => {
  const { userData, updateUserData } = useOnboardingStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const toast = useToast();
  
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const uploadBg = useColorModeValue('gray.50', 'gray.700');
  const uploadHoverBg = useColorModeValue('gray.100', 'gray.600');
  const successColor = useColorModeValue('green.500', 'green.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const educationLevels = [
    {
      label: 'Pre-College',
      options: [
        'High School Student',
        'High School Graduate',
      ],
    },
    {
      label: 'College',
      options: [
        'College Freshman',
        'College Sophomore',
        'College Junior',
        'College Senior',
        'Bachelor’s Degree Holder',
      ],
    },
    {
      label: 'Postgraduate',
      options: [
        'Graduate Student',
        'Master’s Degree Holder',
        'Doctoral Degree Holder',
      ],
    },
    {
      label: '',
      options: [
        'Other',
      ],
    },
  ];

  // Use document upload hook
  const { uploadQueue, startUpload, isUploading } = useDocumentUpload({
    onUploadComplete: (document) => {
      // Store the document ID in the onboarding data
      updateUserData('transcriptUrl', document.doc_id.toString());
      toast({
        title: 'Transcript uploaded successfully',
        description: 'Your transcript has been securely uploaded.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onUploadError: (error) => {
      toast({
        title: 'Upload failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });

  // Get the current upload status
  const currentUpload = uploadQueue[0];
  const uploadProgress = currentUpload?.progress || 0;
  const hasTranscript = userData.transcriptUrl && userData.transcriptUrl !== '';

  // Handle file selection
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Validate file type and size
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PDF or image file.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast({
          title: 'File too large',
          description: 'Please upload a file smaller than 10MB.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      // Start upload with 'other' document type (since transcript isn't a defined type)
      startUpload([file], DocumentType.OTHER);
    }
  }, [startUpload, toast]);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      
      // Validate file type and size
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PDF or image file.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast({
          title: 'File too large',
          description: 'Please upload a file smaller than 10MB.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      // Start upload with 'other' document type (since transcript isn't a defined type)
      startUpload([file], DocumentType.OTHER);
    }
  }, [startUpload, toast]);

  const handleClick = () => {
    fileInputRef.current?.click();
  };


  return (
    <VStack spacing={8} align="stretch" justify="center" maxW="800px" mx="auto" w="full">
        {/* Header */}
        <VStack spacing={4} textAlign="center">
          <Heading size="2xl">Academic Information</Heading>
          <Text color={textColor} fontSize="lg">
            Tell us about your school and where you are in your studies
          </Text>
        </VStack>

        {/* Form */}
        <VStack spacing={6} align="stretch">
        <FormControl isRequired>
          <FormLabel>School/Organization</FormLabel>
          <Input
            placeholder="e.g., UC Berkeley, Stanford University"
            value={userData.school}
            onChange={(e) => updateUserData('school', e.target.value)}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>What is your correct level of education?</FormLabel>
          <Select
            placeholder="Select your education level"
            value={userData.grade}
            onChange={(e) => updateUserData('grade', e.target.value)}
            sx={{
              cursor: 'pointer',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {educationLevels.map((group) => (
              group.label ? (
                <optgroup key={group.label} label={`-- ${group.label} --`}>
                  {group.options.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </optgroup>
              ) : (
                group.options.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))
              )
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <HStack spacing={2} mb={2}>
            <FormLabel mb={0}>Upload Transcript (Optional)</FormLabel>
            <Popover placement="top" closeOnBlur={true} isLazy>
              <PopoverTrigger>
                <Text 
                  fontSize="xs" 
                  color="blue.500" 
                  cursor="pointer"
                  _hover={{ textDecoration: 'underline' }}
                  fontWeight="semibold"
                >
                  Why?
                </Text>
              </PopoverTrigger>
              <PopoverContent bg="blue.600" color="white" maxW="300px">
                <PopoverArrow bg="blue.600" />
                <PopoverBody fontSize="sm">
                  Uploading your transcript helps us identify your strengths and areas where you might need extra support. This allows us to provide more targeted learning recommendations and better personalize your tutoring experience.
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </HStack>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          {/* Upload area */}
          <Box
            p={8}
            bg={isDragging ? uploadHoverBg : uploadBg}
            borderRadius="lg"
            borderWidth="2px"
            borderStyle="dashed"
            borderColor={
              hasTranscript 
                ? successColor
                : isDragging 
                  ? 'blue.400' 
                  : borderColor
            }
            textAlign="center"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ 
              borderColor: hasTranscript ? successColor : 'blue.400',
              bg: uploadHoverBg
            }}
            onClick={handleClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <VStack spacing={3}>
                {React.createElement(FaUpload as unknown as React.ElementType, { size: 32, color: '#4299e1' })}
                <Text color={textColor}>Uploading transcript...</Text>
                <Progress
                  value={uploadProgress}
                  size="sm"
                  colorScheme="blue"
                  width="100%"
                  borderRadius="full"
                />
                <Text fontSize="sm" color="gray.500">
                  {Math.round(uploadProgress)}% complete
                </Text>
              </VStack>
            ) : hasTranscript ? (
              <VStack spacing={3}>
                {React.createElement(FaCheckCircle as unknown as React.ElementType, { size: 32, color: successColor as any })}
                <Text color={textColor} fontWeight="medium">
                  Transcript uploaded successfully
                </Text>
                <HStack spacing={2}>
                  {React.createElement(FaFile as unknown as React.ElementType, { color: 'gray.400' })}
                  <Text fontSize="sm" color="gray.500">
                    Document ID: {userData.transcriptUrl}
                  </Text>
                </HStack>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick();
                  }}
                >
                  Replace Transcript
                </Button>
              </VStack>
            ) : (
              <VStack spacing={2}>
                {React.createElement(FaUpload as unknown as React.ElementType, { size: 32, color: 'gray.400' })}
                <Text color={textColor}>
                  Click to upload or drag and drop
                </Text>
                <Text fontSize="sm" color="gray.500">
                  PDF or image files (max 10MB)
                </Text>
              </VStack>
            )}
          </Box>
        </FormControl>
        </VStack>
      </VStack>
  );
};

export default AcademicStep;